// Account list table — FE-038 / FE-039.
//
// Sortable headers + drill-throughs. Row links route to the
// FE-039 detail page (``/dashboard/accounts/<id>``); inbound
// dashboard URLs of the shape ``?id=<id>`` still hydrate an ``id``
// chip on the list and pin the table to that single row.

import { ArrowDown, ArrowUp, ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";

import { buildCommitmentFilterUrl } from "@/features/dashboard";
import { Table, THead, TBody, Tr, Th, Td } from "@/design-system";

import { AccountStatusBadge } from "./AccountStatusBadge";
import { formatPlanYearDate } from "./dateFormat";
import { RiskBadge } from "@/features/dashboard/accountPerformance";
import type { AccountListRow, AccountListSortKey } from "./types";


export interface AccountListTableProps {
  rows: AccountListRow[];
  total: number;
  sortBy: AccountListSortKey;
  sortDir: "asc" | "desc";
  onSortChange: (next: {
    sortBy: AccountListSortKey;
    sortDir: "asc" | "desc";
  }) => void;
  limit: number;
  offset: number;
  onOffsetChange: (next: number) => void;
}


interface ColumnConfig {
  key:
    | "legal_name" | "status" | "industry" | "employees"
    | "primary_contact" | "renewal_date"
    | "open_commitments" | "overdue_commitments"
    | "risk" | "updated_at";
  label: string;
  sortKey?: AccountListSortKey;
  align?: "left" | "right" | "center";
  testKey?: string;
}


const COLUMNS: ColumnConfig[] = [
  { key: "legal_name", label: "Account",
    sortKey: "legal_name", align: "left", testKey: "legal-name" },
  { key: "status", label: "Status",
    align: "center", testKey: "status" },
  { key: "industry", label: "Industry",
    align: "left", testKey: "industry" },
  { key: "employees", label: "Employees",
    align: "right", testKey: "employees" },
  { key: "primary_contact", label: "Primary contact",
    align: "left", testKey: "primary-contact" },
  { key: "renewal_date", label: "Renewal",
    sortKey: "renewal_date", align: "left", testKey: "renewal" },
  { key: "open_commitments", label: "Open",
    sortKey: "open_commitments", align: "right",
    testKey: "open-commitments" },
  { key: "overdue_commitments", label: "Overdue",
    sortKey: "overdue_commitments", align: "right",
    testKey: "overdue" },
  { key: "risk", label: "Risk", align: "center", testKey: "risk" },
  { key: "updated_at", label: "Updated",
    sortKey: "updated_at", align: "left", testKey: "updated" },
];


export function AccountListTable({
  rows, total, sortBy, sortDir, onSortChange,
  limit, offset, onOffsetChange,
}: AccountListTableProps) {
  const showingFrom = rows.length === 0 ? 0 : offset + 1;
  const showingTo = Math.min(offset + rows.length, total);
  const hasPrev = offset > 0;
  const hasNext = offset + rows.length < total;
  return (
    <div data-testid="account-list-table" className="flex flex-col">
      <Table wrapperClassName="rounded-md border border-border-subtle">
        <THead className="bg-recessed text-xs font-medium uppercase tracking-wide text-text-tertiary">
          <Tr>
            {COLUMNS.map((col) => (
              <Th
                key={col.key}
                className={[
                  col.align === "right" ? "text-right" : "",
                  col.align === "center" ? "text-center" : "",
                ].join(" ")}
                aria-sort={
                  col.sortKey === sortBy
                    ? sortDir === "asc" ? "ascending" : "descending"
                    : col.sortKey ? "none" : undefined
                }
              >
                {col.sortKey ? (
                  <SortHeaderButton
                    label={col.label}
                    active={col.sortKey === sortBy}
                    sortDir={col.sortKey === sortBy ? sortDir : null}
                    onClick={() =>
                      onSortChange(
                        col.sortKey === sortBy
                          ? {
                              sortBy: col.sortKey,
                              sortDir: sortDir === "asc" ? "desc" : "asc",
                            }
                          : { sortBy: col.sortKey!, sortDir: "desc" },
                      )
                    }
                  />
                ) : (
                  col.label
                )}
              </Th>
            ))}
            <Th className="w-8 text-right">
              <span className="sr-only">Drill through</span>
            </Th>
          </Tr>
        </THead>
        <TBody>
          {rows.map((row) => (
            <AccountRow key={row.account_id} row={row} />
          ))}
        </TBody>
      </Table>
      <div className="mt-2 flex items-center justify-between text-xs text-text-tertiary">
        <span data-testid="account-list-pagination-summary">
          {total === 0
            ? "0 accounts"
            : `Showing ${showingFrom}–${showingTo} of ${total}`}
        </span>
        <div className="flex items-center gap-1">
          <button
            type="button"
            data-testid="account-list-prev"
            disabled={!hasPrev}
            onClick={() => onOffsetChange(Math.max(0, offset - limit))}
            aria-label="Previous page"
            className="inline-flex h-7 items-center justify-center rounded-md px-2 text-text-secondary disabled:cursor-not-allowed disabled:text-text-tertiary hover:bg-recessed"
          >
            Prev
          </button>
          <button
            type="button"
            data-testid="account-list-next"
            disabled={!hasNext}
            onClick={() => onOffsetChange(offset + limit)}
            aria-label="Next page"
            className="inline-flex h-7 items-center justify-center rounded-md px-2 text-text-secondary disabled:cursor-not-allowed disabled:text-text-tertiary hover:bg-recessed"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
}


function AccountRow({ row }: { row: AccountListRow }) {
  const detailHref = `/dashboard/accounts/${encodeURIComponent(row.account_id)}`;
  const overdue = row.overdue_commitments ?? 0;
  return (
    <Tr
      data-testid={`account-list-row-${row.account_id}`}
      className="border-t border-border-subtle"
    >
      <Td
        data-testid={`cell-legal-name-${row.account_id}`}
        className="text-text-primary"
      >
        <Link to={detailHref} className="hover:underline">
          {row.legal_name}
        </Link>
        {row.dba_name && (
          <span className="ml-1 text-xs text-text-tertiary">
            ({row.dba_name})
          </span>
        )}
      </Td>
      <Td
        data-testid={`cell-status-${row.account_id}`}
        className="text-center"
      >
        <AccountStatusBadge status={row.status} />
      </Td>
      <Td
        data-testid={`cell-industry-${row.account_id}`}
        className="text-text-secondary"
      >
        {row.industry ?? "—"}
      </Td>
      <Td
        data-testid={`cell-employees-${row.account_id}`}
        className="text-right text-text-primary"
      >
        {row.employee_count_total ?? "—"}
      </Td>
      <Td
        data-testid={`cell-primary-contact-${row.account_id}`}
        className="text-text-secondary"
      >
        {row.primary_contact_name || row.primary_contact_email || "—"}
      </Td>
      <Td
        data-testid={`cell-renewal-${row.account_id}`}
        className="text-text-secondary"
      >
        {row.renewal_date ? formatDate(row.renewal_date) : "—"}
      </Td>
      <Td
        data-testid={`cell-open-commitments-${row.account_id}`}
        className="text-right text-text-primary"
      >
        {row.open_commitments ?? "—"}
      </Td>
      <Td
        data-testid={`cell-overdue-${row.account_id}`}
        className="text-right text-text-primary"
      >
        {overdue > 0 ? (
          <Link
            to={buildCommitmentFilterUrl({
              client_id: row.account_id, overdue: true,
            })}
            data-testid={`account-list-overdue-link-${row.account_id}`}
            className="font-medium text-ember hover:underline"
          >
            {overdue}
          </Link>
        ) : (
          overdue
        )}
      </Td>
      <Td
        data-testid={`cell-risk-${row.account_id}`}
        className="text-center"
      >
        {row.risk_bucket ? <RiskBadge bucket={row.risk_bucket} /> : "—"}
      </Td>
      <Td
        data-testid={`cell-updated-${row.account_id}`}
        className="text-text-secondary"
      >
        {row.updated_at ? formatDate(row.updated_at) : "—"}
      </Td>
      <Td className="text-right">
        <Link
          to={detailHref}
          data-testid={`account-list-drill-${row.account_id}`}
          aria-label={`Open ${row.legal_name}`}
          className="inline-flex h-7 w-7 items-center justify-center rounded-full text-text-tertiary transition-fast ease-out hover:bg-recessed hover:text-text-primary"
        >
          <ChevronRight size={14} aria-hidden="true" />
        </Link>
      </Td>
    </Tr>
  );
}


function SortHeaderButton({
  label, active, sortDir, onClick,
}: {
  label: string;
  active: boolean;
  sortDir: "asc" | "desc" | null;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={
        active
          ? `Sort by ${label}, currently ${sortDir === "asc" ? "ascending" : "descending"}`
          : `Sort by ${label}`
      }
      className={[
        "inline-flex items-center gap-1 transition-fast ease-out",
        active ? "text-text-primary" : "hover:text-text-primary",
      ].join(" ")}
    >
      <span>{label}</span>
      {active && sortDir === "asc" ? (
        <ArrowUp size={10} aria-hidden="true" />
      ) : active && sortDir === "desc" ? (
        <ArrowDown size={10} aria-hidden="true" />
      ) : null}
    </button>
  );
}


function formatDate(iso: string): string {
  // ``renewal_date`` arrives as YYYY-MM-DD — share the TZ-safe
  // helper so the rendered day matches the stored value.
  return formatPlanYearDate(iso);
}
